import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Music, Calendar, ArrowLeft, Diamond } from "lucide-react";
import { api } from "@/lib/api";
import BookingModal from "@/components/BookingModal";
import { Button } from "@/components/ui/button";

export default function ArtistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/artists/${id}`).then((r) => setArtist(r.data)).catch(() => navigate("/artistes"));
  }, [id, navigate]);

  if (!artist)
    return <div className="min-h-[60vh] flex items-center justify-center text-[#A19E95]">Chargement...</div>;

  const images = [artist.image_url, ...(artist.gallery || [])].filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
      <button onClick={() => navigate("/artistes")} className="text-[#A19E95] hover:text-[#C9A24B] flex items-center gap-2 mb-8 text-sm">
        <ArrowLeft size={16} /> Retour aux artistes
      </button>

      <div className="grid lg:grid-cols-2 gap-12">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <div className="rounded-2xl overflow-hidden border border-[#C9A24B]/20">
            <img src={artist.image_url} alt={artist.name} className="w-full aspect-[4/5] object-cover" />
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-3 gap-3 mt-3">
              {images.slice(1, 4).map((img, i) => (
                <img key={i} src={img} alt="" className="w-full h-24 object-cover rounded-xl border border-white/10" />
              ))}
            </div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
          {artist.trend && (
            <span className="inline-block gold-bg text-black text-[10px] font-bold px-3 py-1 rounded-full mb-4">
              ARTISTE TENDANCE
            </span>
          )}
          <h1 className="font-serif text-5xl text-white">{artist.name}</h1>
          <div className="flex flex-wrap gap-4 mt-4 text-sm text-[#A19E95]">
            <span className="flex items-center gap-1.5"><Music size={15} className="text-[#C9A24B]" /> {artist.genre} · {artist.style}</span>
            <span className="flex items-center gap-1.5"><MapPin size={15} className="text-[#C9A24B]" /> {artist.continent}</span>
          </div>

          <div className="mt-6 p-5 rounded-2xl border border-[#C9A24B]/25 glass">
            <p className="text-[10px] uppercase tracking-wider text-[#A19E95]">Tarif indicatif</p>
            <p className="font-serif text-3xl gold-text mt-1">{artist.price_display}</p>
            <p className="text-xs text-[#6E6B63] mt-2">
              Le tarif final vous sera communiqué sur devis après étude de votre événement.
            </p>
          </div>

          <p className="text-[#C4C1B8] leading-relaxed mt-6">{artist.bio}</p>

          {artist.availability?.length > 0 && (
            <div className="mt-6">
              <p className="text-white font-medium mb-3 flex items-center gap-2">
                <Calendar size={16} className="text-[#C9A24B]" /> Créneaux disponibles
              </p>
              <div className="flex flex-wrap gap-2">
                {artist.availability.map((d) => (
                  <span key={d} className="text-xs text-[#C9A24B] border border-[#C9A24B]/30 px-3 py-1.5 rounded-full">
                    {new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                  </span>
                ))}
              </div>
            </div>
          )}

          <Button
            onClick={() => setOpen(true)}
            data-testid="open-booking-btn"
            className="w-full mt-8 gold-bg text-black font-semibold rounded-full py-6 text-base hover:opacity-90"
          >
            <Diamond size={18} className="mr-2" /> Demander une disponibilité / un devis
          </Button>
        </motion.div>
      </div>

      <BookingModal artist={artist} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
